/**
 * L'ÉCHÉANCIER — lot 97, 18/09/2026.
 *
 * Maquette `maquette-analyse-echeancier-v2.html`, cadrage « Analyse
 * financière : 2. l'échéancier » et « 8. les arbitrages ».
 *
 * Deux onglets (à encaisser · à payer), quatre tuiles qui filtrent, la courbe
 * des semaines à venir, le tableau groupé par semaine prévue où la date se
 * change en place, la saisie groupée, et le panneau d'une pièce : paiement en
 * plusieurs fois, historique, règlements du tiers, cession au factor ou
 * réglage du fournisseur.
 *
 * LE CONTENEUR APPELLE, LA VUE DESSINE. Tout calcul est dans `echeancier.ts`
 * et `@stafa/core` ; ce fichier n'en contient aucun.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ajouterJours, centimes, ecartJours } from '@stafa/core';
import type { EchecApi } from './api.js';
import {
  lireDetailPiece,
  lireEcheancier,
  poserCession,
  poserEnGroupe,
  poserPaiements,
  reglerFournisseur,
  type DetailPiece,
} from './apiAnalyse.js';
import {
  aujourdhuiLocal,
  courbe,
  dateCourte,
  dateFr,
  etiquettes,
  euros,
  filtrer,
  grouperParSemaine,
  lignesDe,
  origineEnMots,
  resteADecouper,
  SANS_FILTRE,
  semainesAVenir,
  tiersDe,
  trier,
  tuiles,
  type CleTri,
  type CoteEcheancier,
  type EcheancierLu,
  type Filtres,
  type LigneEcheancier,
  type PaiementPrevu,
  type PieceEcheancier,
  type Quand,
} from './echeancier.js';
import { fraicheur } from './sources.js';
import { BandeauFiabilite } from './BandeauFiabilite.js';

/** Ce qu'un geste rend : rien si tout va bien, sinon la phrase à afficher. */
type Issue = Promise<string | null>;

export interface ActionsEcheancier {
  readonly poser: (
    cote: CoteEcheancier,
    id: string,
    paiements: readonly PaiementPrevu[],
    commentaire: string | null,
  ) => Issue;
  /** Rend le refus, ou une information à afficher en cas de succès. */
  readonly poserGroupe: (
    cote: CoteEcheancier,
    ids: readonly string[],
    date: string | null,
  ) => Promise<{ readonly refus: string | null; readonly info: string | null }>;
  readonly ceder: (id: string, date: string | null) => Issue;
  readonly reglerFournisseur: (
    id: string,
    reglage: { prelevement?: boolean | null; centre_id?: string | null },
  ) => Issue;
  readonly detail: (cote: CoteEcheancier, id: string) => Promise<DetailPiece | string>;
}

export function EcranEcheancier({
  jeton,
  quandAccesPerdu,
  messageRefus,
  sources,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  /** Le panneau des sources Pennylane, rendu par l'hôte. */
  readonly sources: (quandLu: () => void) => ReactNode;
}) {
  const [cote, setCote] = useState<CoteEcheancier>('client');
  const [donnees, setDonnees] = useState<Partial<Record<CoteEcheancier, EcheancierLu>>>(
    {},
  );
  const [refus, setRefus] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);

  async function avecJeton<T>(travail: (j: string) => Promise<T>): Promise<T | null> {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return null;
    }
    return travail(j);
  }

  function phrase(r: { cause: EchecApi; message?: string }): string {
    if (r.cause === 'acces') quandAccesPerdu();
    return r.message ?? messageRefus(r.cause);
  }

  async function charger(quel: CoteEcheancier) {
    setChargement(true);
    setRefus(null);
    const r = await avecJeton((j) => lireEcheancier(j, quel));
    setChargement(false);
    if (r === null) return;
    if (r.etat === 'ok') setDonnees((d) => ({ ...d, [quel]: r.valeur }));
    else setRefus(phrase(r));
  }

  useEffect(() => {
    void charger(cote);
  }, [jeton, cote]);

  const actions: ActionsEcheancier = {
    poser: async (c, id, paiements, commentaire) => {
      const r = await avecJeton((j) =>
        poserPaiements(j, c, id, paiements, commentaire),
      );
      if (r === null) return 'Session terminée.';
      if (r.etat !== 'ok') return phrase(r);
      const piece = r.valeur;
      setDonnees((d) => {
        const lu = d[c];
        if (lu === undefined) return d;
        return {
          ...d,
          [c]: {
            ...lu,
            pieces: lu.pieces.map((p) => (p.pennylane_id === id ? piece : p)),
          },
        };
      });
      return null;
    },
    poserGroupe: async (c, ids, date) => {
      const r = await avecJeton((j) => poserEnGroupe(j, c, ids, date));
      if (r === null) return { refus: 'Session terminée.', info: null };
      if (r.etat !== 'ok') return { refus: phrase(r), info: null };
      await charger(c);
      const v = r.valeur;
      return {
        refus: null,
        info: `${String(v.modifiees)} pièce(s) modifiée(s)${
          v.prelevements > 0
            ? ` · ${String(v.prelevements)} prélèvement(s) laissé(s) à leur échéance`
            : ''
        }${v.introuvables > 0 ? ` · ${String(v.introuvables)} pièce(s) déjà réglée(s) entre-temps` : ''}.`,
      };
    },
    ceder: async (id, date) => {
      const r = await avecJeton((j) => poserCession(j, id, date));
      if (r === null) return 'Session terminée.';
      if (r.etat !== 'ok') return phrase(r);
      setDonnees((d) => {
        const lu = d.client;
        if (lu === undefined) return d;
        return {
          ...d,
          client: {
            ...lu,
            pieces: lu.pieces.map((p) =>
              p.pennylane_id === id ? { ...p, cedee_le: date } : p,
            ),
          },
        };
      });
      return null;
    },
    reglerFournisseur: async (id, reglage) => {
      const r = await avecJeton((j) => reglerFournisseur(j, id, reglage));
      if (r === null) return 'Session terminée.';
      if (r.etat !== 'ok') return phrase(r);
      await charger('fournisseur');
      return null;
    },
    detail: async (c, id) => {
      const r = await avecJeton((j) => lireDetailPiece(j, c, id));
      if (r === null) return 'Session terminée.';
      return r.etat === 'ok' ? r.valeur : phrase(r);
    },
  };

  return (
    <VueEcheancier
      cote={cote}
      quandCote={setCote}
      donnees={donnees[cote] ?? null}
      comptes={{
        client: donnees.client?.pieces.length ?? null,
        fournisseur: donnees.fournisseur?.pieces.length ?? null,
      }}
      chargement={chargement}
      refus={refus}
      actions={actions}
      maintenant={new Date()}
      sources={sources(() => void charger(cote))}
      fiabilite={<BandeauFiabilite jeton={jeton} ecran="echeancier" />}
    />
  );
}

// ---------------------------------------------------------------------------
// La vue
// ---------------------------------------------------------------------------

const QUANDS: readonly { readonly cle: Quand; readonly nom: string }[] = [
  { cle: 'tout', nom: 'Toutes' },
  { cle: 'retard', nom: 'En retard (échéance passée)' },
  { cle: 'revoir', nom: 'Date dépassée — à revoir' },
  { cle: 'calculee', nom: 'Calculée (jamais saisie)' },
  { cle: 'saisie', nom: 'Saisie' },
  { cle: 'semaine', nom: 'Cette semaine' },
  { cle: '4sem', nom: 'Dans les 4 semaines' },
];

export function VueEcheancier({
  cote,
  quandCote,
  donnees,
  comptes,
  chargement,
  refus,
  actions,
  maintenant,
  sources,
  fiabilite = null,
  panneauInitial = null,
  sourcesOuvertes = false,
}: {
  readonly cote: CoteEcheancier;
  readonly quandCote: (c: CoteEcheancier) => void;
  readonly donnees: EcheancierLu | null;
  readonly comptes: Readonly<Record<CoteEcheancier, number | null>>;
  readonly chargement: boolean;
  readonly refus: string | null;
  readonly actions: ActionsEcheancier;
  readonly maintenant: Date;
  readonly sources: ReactNode;
  /** Le bandeau de fiabilite (lot 108). Nul dans l'apercu de maquette. */
  readonly fiabilite?: ReactNode;
  readonly panneauInitial?: string | null;
  readonly sourcesOuvertes?: boolean;
}) {
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const [grouper, setGrouper] = useState(true);
  const [tri, setTri] = useState<{ cle: CleTri; sens: 1 | -1 }>({
    cle: 'prevue',
    sens: 1,
  });
  const [coches, setCoches] = useState<ReadonlySet<string>>(new Set());
  const [dateMasse, setDateMasse] = useState('');
  const [message, setMessage] = useState<{ texte: string; refus: boolean } | null>(
    null,
  );
  const [panneau, setPanneau] = useState<string | null>(panneauInitial);
  const [voirSources, setVoirSources] = useState(sourcesOuvertes);
  const [enCours, setEnCours] = useState(false);

  // Changer d'onglet repart d'un tableau propre : une coche d'un côté n'a pas
  // de sens de l'autre.
  useEffect(() => {
    setFiltres(SANS_FILTRE);
    setCoches(new Set());
    setPanneau(panneauInitial);
  }, [cote]);

  /**
   * JAMAIS LA CHAÎNE VIDE — corrigé le 18/09/2026 après un écran blanc en
   * production. Tant que l'API n'a pas répondu, on prend le jour du
   * navigateur : les calculs ci-dessous tournent alors sur des listes vides,
   * au lieu de lever sur une date illisible et d'emporter l'application.
   */
  const aujourdhui = donnees?.aujourdhui ?? aujourdhuiLocal();
  const toutes = useMemo(
    () => (donnees === null ? [] : lignesDe(donnees.pieces, aujourdhui)),
    [donnees, aujourdhui],
  );
  const visibles = useMemo(
    () => trier(filtrer(toutes, filtres, aujourdhui), tri.cle, tri.sens),
    [toutes, filtres, tri, aujourdhui],
  );
  const semaines = useMemo(
    () => (aujourdhui === '' ? [] : semainesAVenir(aujourdhui)),
    [aujourdhui],
  );
  const t = useMemo(() => tuiles(toutes, aujourdhui), [toutes, aujourdhui]);
  const c = useMemo(() => courbe(visibles, semaines), [visibles, semaines]);
  const clients = cote === 'client';
  const nomsActivite = new Map((donnees?.activites ?? []).map((a) => [a.id, a.nom]));
  const piecesCochees = [...new Set([...coches].map((k) => k.split('#')[0] as string))];

  async function geste(travail: () => Issue, reussite?: string) {
    setEnCours(true);
    const issue = await travail();
    setEnCours(false);
    if (issue === null) {
      if (reussite !== undefined) setMessage({ texte: reussite, refus: false });
      return true;
    }
    setMessage({ texte: issue, refus: true });
    return false;
  }

  async function gesteGroupe(date: string | null) {
    setEnCours(true);
    const r = await actions.poserGroupe(cote, piecesCochees, date);
    setEnCours(false);
    setMessage({ texte: r.refus ?? r.info ?? '', refus: r.refus !== null });
    if (r.refus === null) setCoches(new Set());
  }

  const pieceOuverte =
    panneau === null
      ? undefined
      : donnees?.pieces.find((p) => p.pennylane_id === panneau);

  return (
    <main className="corps corps-analyse">
      <div className="ech-onglets" role="tablist">
        {(['client', 'fournisseur'] as const).map((x) => (
          <button
            key={x}
            type="button"
            role="tab"
            aria-selected={cote === x}
            className={cote === x ? 'ech-onglet actif' : 'ech-onglet'}
            onClick={() => quandCote(x)}
          >
            {x === 'client' ? 'À encaisser · clients' : 'À payer · fournisseurs'}
            {comptes[x] !== null && <span className="ech-nb">{comptes[x]}</span>}
          </button>
        ))}
        <span className="ech-copie">
          Chiffres Pennylane : {fraicheur(donnees?.lu_le ?? null, maintenant)}
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={voirSources}
            onClick={() => setVoirSources((v) => !v)}
          >
            Données Pennylane
          </button>
        </span>
      </div>

      {voirSources && <div className="ech-sources">{sources}</div>}
      {fiabilite}
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
      {message !== null && (
        <p
          className={message.refus ? 'bandeau bandeau-refus' : 'bandeau bandeau-fait'}
          role="status"
        >
          {message.texte}
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </p>
      )}

      {donnees === null ? (
        <p className="attente">
          {chargement ? 'Lecture de l’échéancier…' : 'Aucune donnée.'}
        </p>
      ) : (
        <>
          <div className="ech-tuiles">
            <Tuile
              actif={filtres.quand === 'tout' && filtres.semaine === null}
              titre={clients ? 'Reste à encaisser' : 'Reste à payer'}
              valeur={euros(t.reste)}
              sous={`${String(t.pieces)} pièce(s)`}
              quand={() => setFiltres({ ...filtres, quand: 'tout', semaine: null })}
            />
            <Tuile
              actif={filtres.quand === 'retard'}
              alerte={t.echues.n > 0}
              titre="Échéance passée"
              valeur={euros(t.echues.montant)}
              sous={`${String(t.echues.n)} pièce(s)`}
              quand={() => setFiltres({ ...filtres, quand: 'retard', semaine: null })}
            />
            <Tuile
              actif={filtres.quand === 'revoir'}
              alerte={t.revoir.n > 0}
              titre="Date prévue dépassée — à revoir"
              valeur={String(t.revoir.n)}
              sous={`${euros(t.revoir.montant)} · paiements compris`}
              quand={() => setFiltres({ ...filtres, quand: 'revoir', semaine: null })}
            />
            <Tuile
              actif={filtres.quand === 'calculee'}
              titre="Date jamais saisie"
              valeur={`${String(t.partCalculee)} %`}
              sous={`${euros(t.montantCalcule)} en date calculée`}
              quand={() => setFiltres({ ...filtres, quand: 'calculee', semaine: null })}
            />
          </div>

          <section className="ech-carte">
            <div className="ech-carte-tete">
              <h2>
                {clients
                  ? 'Encaissements prévus, semaine par semaine'
                  : 'Paiements prévus, semaine par semaine'}
              </h2>
              <p className="ech-sous">
                Somme des restes dus selon leur date prévue · suit les filtres du
                tableau · un clic sur une semaine filtre le tableau
              </p>
            </div>
            <p className="ech-legende">
              <span>
                <span className="ech-cle ech-cle-tout" />
                toutes les dates prévues (saisies + calculées)
              </span>
              <span>
                <span className="ech-cle ech-cle-saisies" />
                dates saisies et prélèvements seulement
              </span>
            </p>
            <Courbe
              points={c.points}
              auDela={c.au_dela}
              nAuDela={c.n_au_dela}
              semaineChoisie={filtres.semaine}
              quandSemaine={(lundi) =>
                setFiltres({
                  ...filtres,
                  semaine: filtres.semaine === lundi ? null : lundi,
                })
              }
            />
          </section>

          <section className="ech-carte">
            <div className="ech-outils">
              <label className="ech-champ">
                Rechercher
                <input
                  type="search"
                  value={filtres.texte}
                  placeholder={clients ? 'Numéro, client…' : 'Numéro, fournisseur…'}
                  onChange={(e) => setFiltres({ ...filtres, texte: e.target.value })}
                />
              </label>
              <label className="ech-champ">
                Activité
                <select
                  value={filtres.activite}
                  onChange={(e) => setFiltres({ ...filtres, activite: e.target.value })}
                >
                  <option value="tout">Toutes</option>
                  {donnees.activites.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nom}
                    </option>
                  ))}
                  <option value="sans">Sans activité</option>
                </select>
              </label>
              <label className="ech-champ">
                {clients ? 'Client' : 'Fournisseur'}
                <select
                  value={filtres.tiers}
                  onChange={(e) => setFiltres({ ...filtres, tiers: e.target.value })}
                >
                  <option value="tout">Tous</option>
                  {tiersDe(donnees.pieces).map((x) => (
                    <option key={x.cle} value={x.cle}>
                      {x.nom}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ech-champ">
                Date prévue
                <select
                  value={filtres.quand}
                  onChange={(e) =>
                    setFiltres({ ...filtres, quand: e.target.value as Quand })
                  }
                >
                  {QUANDS.map((q) => (
                    <option key={q.cle} value={q.cle}>
                      {q.nom}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ech-bascule">
                <input
                  type="checkbox"
                  checked={grouper}
                  onChange={(e) => setGrouper(e.target.checked)}
                />
                Grouper par semaine prévue
              </label>
              {filtres.semaine !== null && (
                <button
                  type="button"
                  className="bouton bouton-discret bouton-petit"
                  onClick={() => setFiltres({ ...filtres, semaine: null })}
                >
                  Semaine du {dateCourte(filtres.semaine)} ×
                </button>
              )}
            </div>

            {coches.size > 0 && (
              <div className="ech-bande">
                <strong>{piecesCochees.length} pièce(s) cochée(s)</strong>
                <label>
                  Date prévue pour la sélection :{' '}
                  <input
                    type="date"
                    value={dateMasse}
                    onChange={(e) => setDateMasse(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="bouton bouton-petit"
                  disabled={dateMasse === '' || enCours}
                  onClick={() => void gesteGroupe(dateMasse)}
                >
                  Appliquer
                </button>
                <button
                  type="button"
                  className="bouton bouton-discret bouton-petit"
                  disabled={enCours}
                  onClick={() => void gesteGroupe(null)}
                >
                  Revenir à la date calculée
                </button>
                <button
                  type="button"
                  className="ech-lien"
                  onClick={() => setCoches(new Set())}
                >
                  Tout décocher
                </button>
              </div>
            )}

            <Tableau
              lignes={visibles}
              grouper={grouper && tri.cle === 'prevue'}
              clients={clients}
              aujourdhui={aujourdhui}
              nomsActivite={nomsActivite}
              tri={tri}
              quandTri={(cle) =>
                setTri((x) => ({ cle, sens: x.cle === cle ? (-x.sens as 1 | -1) : 1 }))
              }
              coches={coches}
              quandCoche={(cles, oui) =>
                setCoches((s) => {
                  const n = new Set(s);
                  for (const k of cles) {
                    if (oui) n.add(k);
                    else n.delete(k);
                  }
                  return n;
                })
              }
              enCours={enCours}
              quandDate={(l, date) => {
                void geste(() =>
                  actions.poser(
                    cote,
                    l.piece.pennylane_id,
                    nouveauxPaiements(l, date),
                    null,
                  ),
                );
              }}
              quandCalcul={(l) => {
                void geste(
                  () => actions.poser(cote, l.piece.pennylane_id, [], null),
                  'Retour à la date calculée.',
                );
              }}
              quandOuvrir={(id) => setPanneau(id)}
            />
          </section>
        </>
      )}

      {pieceOuverte !== undefined && donnees !== null && (
        <PanneauPiece
          piece={pieceOuverte}
          cote={cote}
          aujourdhui={aujourdhui}
          toutes={donnees.pieces}
          activites={donnees.activites}
          actions={actions}
          quandFermer={() => setPanneau(null)}
          quandOuvrir={(id) => setPanneau(id)}
        />
      )}
    </main>
  );
}

/**
 * LA NOUVELLE LISTE DE PAIEMENTS quand on change la date d'UNE ligne : une
 * pièce entière prend la date pour tout son reste ; un paiement d'une pièce
 * découpée ne change que sa propre date.
 */
function nouveauxPaiements(l: LigneEcheancier, date: string): PaiementPrevu[] {
  if (l.part.origine !== 'echeancier') return [{ montant: null, date }];
  const tries = [...l.piece.paiements].sort((a, b) => (a.date < b.date ? -1 : 1));
  return tries.map((p, i) => (i === l.part.rang ? { ...p, date } : p));
}

function Tuile({
  titre,
  valeur,
  sous,
  actif,
  alerte = false,
  quand,
}: {
  readonly titre: string;
  readonly valeur: string;
  readonly sous: string;
  readonly actif: boolean;
  readonly alerte?: boolean;
  readonly quand: () => void;
}) {
  return (
    <button
      type="button"
      className={`ech-tuile${actif ? ' actif' : ''}${alerte ? ' alerte' : ''}`}
      aria-pressed={actif}
      onClick={quand}
    >
      <span className="ech-tuile-titre">{titre}</span>
      <span className="ech-tuile-valeur">{valeur}</span>
      <span className="ech-tuile-sous">{sous}</span>
    </button>
  );
}

function Courbe({
  points,
  auDela,
  nAuDela,
  semaineChoisie,
  quandSemaine,
}: {
  readonly points: ReturnType<typeof courbe>['points'];
  readonly auDela: number;
  readonly nAuDela: number;
  readonly semaineChoisie: string | null;
  readonly quandSemaine: (lundi: string) => void;
}) {
  // LA LARGEUR EST MESURÉE, pas étirée : un dessin mis à l'échelle grossit
  // ses textes avec lui (constaté sur l'aperçu à 1 440 px).
  const cadre = useRef<HTMLDivElement>(null);
  const [largeur, setLargeur] = useState(760);
  useEffect(() => {
    const zone = cadre.current;
    if (zone === null || typeof ResizeObserver === 'undefined') return;
    const observateur = new ResizeObserver((entrees) => {
      const w = entrees[0]?.contentRect.width;
      if (w !== undefined && w > 0) setLargeur(Math.round(w));
    });
    observateur.observe(zone);
    return () => observateur.disconnect();
  }, []);
  const L = Math.max(300, largeur);
  const H = 240;
  const g = 64;
  const d = 12;
  const h = 16;
  const b = 40;
  const max = Math.max(100000, ...points.map((p) => p.tout)) * 1.12;
  const pasBase = Math.pow(10, Math.floor(Math.log10(max / 4)));
  const pas =
    [1, 2, 2.5, 5, 10].map((k) => k * pasBase).find((k) => k >= max / 4) ?? pasBase;
  const graduations: number[] = [];
  for (let v = 0; v <= max + pas / 2; v += pas) graduations.push(v);
  const haut = graduations[graduations.length - 1] ?? max;
  const col = (L - g - d) / Math.max(1, points.length);
  const x = (i: number) => g + col * i + col / 2;
  const y = (v: number) => h + (1 - v / haut) * (H - h - b);
  const ligne = (cle: 'tout' | 'saisies') =>
    points.map((p, i) => `${x(i).toFixed(1)},${y(p[cle]).toFixed(1)}`).join(' ');

  return (
    <div className="ech-courbe">
      <div ref={cadre} className="ech-dessin">
        <svg
          width={L}
          height={H}
          viewBox={`0 0 ${String(L)} ${String(H)}`}
          role="img"
          aria-label="Montants prévus par semaine"
        >
          {graduations.map((v) => (
            <g key={v}>
              <line x1={g} x2={L - d} y1={y(v)} y2={y(v)} className="ech-grille" />
              <text x={g - 8} y={y(v) + 4} textAnchor="end" className="ech-axe">
                {v >= 100000 ? `${String(Math.round(v / 100000))} k€` : euros(v)}
              </text>
            </g>
          ))}
          {points.map((p, i) => (
            <g
              key={p.semaine.lundi}
              className={`ech-colonne${semaineChoisie === p.semaine.lundi ? ' choisie' : ''}`}
              onClick={() => quandSemaine(p.semaine.lundi)}
              role="button"
              tabIndex={0}
              aria-label={`Semaine ${String(p.semaine.numero)} : ${euros(p.tout)}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') quandSemaine(p.semaine.lundi);
              }}
            >
              <rect
                x={g + col * i + 2}
                y={h}
                width={col - 4}
                height={H - h - b}
                className="ech-zone"
              />
              <text x={x(i)} y={H - b + 16} textAnchor="middle" className="ech-axe">
                S{p.semaine.numero}
              </text>
              <text
                x={x(i)}
                y={H - b + 30}
                textAnchor="middle"
                className="ech-axe ech-axe-doux"
              >
                {dateCourte(p.semaine.lundi)}
              </text>
              <title>{`S${String(p.semaine.numero)} : ${euros(p.tout)} (${String(p.n)} ligne(s)), dont ${euros(p.saisies)} en dates saisies`}</title>
            </g>
          ))}
          <polyline points={ligne('tout')} className="ech-serie ech-serie-tout" />
          <polyline points={ligne('saisies')} className="ech-serie ech-serie-saisies" />
          {points.map((p, i) => (
            <circle
              key={`p${p.semaine.lundi}`}
              cx={x(i)}
              cy={y(p.tout)}
              r={3.5}
              className="ech-point"
            />
          ))}
        </svg>
      </div>
      <div className="ech-au-dela">
        <span>Au-delà</span>
        <strong>{euros(auDela)}</strong>
        <span className="ech-sous">{nAuDela} ligne(s)</span>
      </div>
    </div>
  );
}

const COLONNES: readonly {
  cle: CleTri | null;
  nom: (clients: boolean) => string;
  nombre?: boolean;
  /** Masquée quand la place manque : l'information est aussi dans le panneau. */
  secondaire?: boolean;
}[] = [
  { cle: 'piece', nom: (c) => (c ? 'Facture' : 'Pièce') },
  { cle: 'tiers', nom: (c) => (c ? 'Client' : 'Fournisseur') },
  { cle: null, nom: () => 'Activité', secondaire: true },
  { cle: 'date', nom: () => 'Date', secondaire: true },
  { cle: 'echeance', nom: () => 'Échéance' },
  { cle: 'reste', nom: () => 'Reste dû TTC', nombre: true },
  {
    cle: 'retard',
    nom: (c) => (c ? 'Retard habituel' : 'Délai habituel'),
    nombre: true,
  },
  { cle: 'prevue', nom: (c) => (c ? 'Encaissement prévu' : 'Paiement prévu') },
];

function Tableau({
  lignes,
  grouper,
  clients,
  aujourdhui,
  nomsActivite,
  tri,
  quandTri,
  coches,
  quandCoche,
  enCours,
  quandDate,
  quandCalcul,
  quandOuvrir,
}: {
  readonly lignes: readonly LigneEcheancier[];
  readonly grouper: boolean;
  readonly clients: boolean;
  readonly aujourdhui: string;
  readonly nomsActivite: ReadonlyMap<string, string>;
  readonly tri: { cle: CleTri; sens: 1 | -1 };
  readonly quandTri: (cle: CleTri) => void;
  readonly coches: ReadonlySet<string>;
  readonly quandCoche: (cles: readonly string[], oui: boolean) => void;
  readonly enCours: boolean;
  readonly quandDate: (l: LigneEcheancier, date: string) => void;
  readonly quandCalcul: (l: LigneEcheancier) => void;
  readonly quandOuvrir: (id: string) => void;
}) {
  if (lignes.length === 0) {
    return <p className="ech-vide">Aucune pièce ne correspond aux filtres.</p>;
  }
  const cochables = lignes
    .filter((l) => l.part.origine !== 'prelevement')
    .map((l) => l.cle);
  const toutCoche = cochables.length > 0 && cochables.every((k) => coches.has(k));
  const colonnes = COLONNES.length + 2 + (clients ? 1 : 0);
  const groupes = grouper
    ? grouperParSemaine(lignes)
    : [{ lundi: '', numero: 0, total: 0, lignes }];

  return (
    <div className="ech-tableau">
      <table>
        <thead>
          <tr>
            <th className="ech-case">
              <input
                type="checkbox"
                aria-label="Tout cocher"
                checked={toutCoche}
                onChange={(e) => quandCoche(cochables, e.target.checked)}
              />
            </th>
            {COLONNES.map((c) => (
              <th
                key={c.nom(clients)}
                className={
                  c.nombre === true
                    ? 'nombre'
                    : c.secondaire === true
                      ? 'ech-secondaire'
                      : undefined
                }
              >
                {c.cle === null ? (
                  c.nom(clients)
                ) : (
                  <button
                    type="button"
                    className="ech-tri"
                    onClick={() => quandTri(c.cle as CleTri)}
                  >
                    {c.nom(clients)}
                    {tri.cle === c.cle ? (tri.sens === 1 ? ' ▲' : ' ▼') : ''}
                  </button>
                )}
              </th>
            ))}
            {clients && <th>Promesse client</th>}
            <th>Origine</th>
          </tr>
        </thead>
        <tbody>
          {groupes.map((gr) => (
            <GroupeLignes
              key={gr.lundi || 'tout'}
              titre={
                grouper
                  ? `Semaine ${String(gr.numero)} · du ${dateCourte(gr.lundi)} au ${dateCourte(ajouterJours(gr.lundi, 6))}`
                  : null
              }
              total={gr.total}
              colonnes={colonnes}
            >
              {gr.lignes.map((l) => (
                <LigneTableau
                  key={l.cle}
                  l={l}
                  clients={clients}
                  aujourdhui={aujourdhui}
                  activite={
                    l.piece.activite_id === null
                      ? '—'
                      : (nomsActivite.get(l.piece.activite_id) ?? '—')
                  }
                  coche={coches.has(l.cle)}
                  quandCoche={(oui) => quandCoche([l.cle], oui)}
                  enCours={enCours}
                  quandDate={(d) => quandDate(l, d)}
                  quandCalcul={() => quandCalcul(l)}
                  quandOuvrir={() => quandOuvrir(l.piece.pennylane_id)}
                />
              ))}
            </GroupeLignes>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupeLignes({
  titre,
  total,
  colonnes,
  children,
}: {
  readonly titre: string | null;
  readonly total: number;
  readonly colonnes: number;
  readonly children: ReactNode;
}) {
  return (
    <>
      {titre !== null && (
        <tr className="ech-groupe">
          <td colSpan={3}>{titre}</td>
          <td className="ech-secondaire" colSpan={2} />
          <td />
          <td className="nombre">{euros(total, true)}</td>
          <td colSpan={colonnes - 7} />
        </tr>
      )}
      {children}
    </>
  );
}

function LigneTableau({
  l,
  clients,
  aujourdhui,
  activite,
  coche,
  quandCoche,
  enCours,
  quandDate,
  quandCalcul,
  quandOuvrir,
}: {
  readonly l: LigneEcheancier;
  readonly clients: boolean;
  readonly aujourdhui: string;
  readonly activite: string;
  readonly coche: boolean;
  readonly quandCoche: (oui: boolean) => void;
  readonly enCours: boolean;
  readonly quandDate: (d: string) => void;
  readonly quandCalcul: () => void;
  readonly quandOuvrir: () => void;
}) {
  const p = l.piece;
  const prelevement = l.part.origine === 'prelevement';
  const saisie = l.part.origine === 'saisie' || l.part.origine === 'echeancier';
  const marques = etiquettes(l, aujourdhui);
  return (
    /*
      LA LIGNE ENTIÈRE OUVRE LA PIÈCE — lot 114, 22/09/2026.

      Seul le NUMÉRO était cliquable ici, alors que l'onglet Factures ouvre sa
      fenêtre depuis la ligne entière (`EcranFactures.tsx`, `<tr onClick>`).
      Deux écrans, deux gestes, et rien ne disait lequel : l'exploitation a
      cliqué sur la ligne et a cru le panneau cassé.

      Le bouton du numéro RESTE : il porte le nom accessible de la pièce et
      s'atteint au clavier, ce qu'une ligne de tableau ne fait pas. Son clic
      est arrêté ici pour ne pas ouvrir deux fois.
    */
    <tr
      className={l.part.a_revoir ? 'ech-a-revoir' : undefined}
      onClick={quandOuvrir}
    >
      <td className="ech-case" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`Cocher ${p.numero ?? p.pennylane_id}`}
          checked={coche}
          disabled={prelevement}
          onChange={(e) => quandCoche(e.target.checked)}
        />
      </td>
      <td>
        <button
          type="button"
          className="ech-numero"
          onClick={(e) => {
            e.stopPropagation();
            quandOuvrir();
          }}
        >
          {p.numero ?? `n° ${p.pennylane_id}`}
        </button>
        {marques.length > 0 && (
          <span className="ech-etiquettes">
            {marques.map((m) => (
              <span
                key={m}
                className={`ech-etiquette${m.startsWith('échue') || m === 'à revoir' ? ' alerte' : ''}`}
              >
                {m}
              </span>
            ))}
          </span>
        )}
      </td>
      <td className="ech-tiers">{p.tiers.nom}</td>
      <td className="ech-doux ech-secondaire" data-etiquette="Activité">
        {activite}
      </td>
      <td className="ech-secondaire" data-etiquette="Date">
        {dateFr(p.date_piece)}
      </td>
      <td data-etiquette="Échéance">{dateFr(p.echeance)}</td>
      <td className="nombre ech-montant" data-etiquette="Reste dû">
        {euros(l.part.montant_centimes, true)}
      </td>
      <td
        className="nombre ech-doux"
        data-etiquette={clients ? 'Retard habituel' : 'Délai habituel'}
      >
        {p.retard.jours === null
          ? '—'
          : `${p.retard.jours > 0 ? '+' : ''}${String(p.retard.jours)} j`}
      </td>
      <td className="ech-prevue" data-etiquette="Prévu le">
        <input
          type="date"
          aria-label="Date prévue"
          className={saisie ? 'saisie' : 'calculee'}
          value={l.part.date}
          disabled={prelevement || enCours}
          onChange={(e) => {
            if (e.target.value !== '') quandDate(e.target.value);
          }}
        />
        {l.part.a_revoir && l.part.date_brute !== l.part.date && (
          <span className="ech-doux ech-petit">
            prévue le {dateFr(l.part.date_brute)}
          </span>
        )}
      </td>
      {clients && (
        <td className="ech-petit" data-etiquette="Promesse">
          {p.promesse === null ? (
            <span className="ech-doux">—</span>
          ) : (
            <>
              {dateFr(p.promesse.date)}
              {p.promesse.date !== l.part.date_brute && l.part.nombre === 1 && (
                <button
                  type="button"
                  className="ech-lien"
                  disabled={enCours}
                  onClick={() => quandDate(p.promesse?.date ?? l.part.date)}
                >
                  reprendre cette date
                </button>
              )}
            </>
          )}
        </td>
      )}
      <td className="ech-petit ech-doux ech-origine">
        {origineEnMots(l)}
        {saisie && (
          <button
            type="button"
            className="ech-lien"
            disabled={enCours}
            onClick={quandCalcul}
          >
            revenir au calcul
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Le panneau d'une pièce
// ---------------------------------------------------------------------------

interface LigneSaisie {
  montant: string;
  date: string;
}

function PanneauPiece({
  piece,
  cote,
  aujourdhui,
  toutes,
  activites,
  actions,
  quandFermer,
  quandOuvrir,
}: {
  readonly piece: PieceEcheancier;
  readonly cote: CoteEcheancier;
  readonly aujourdhui: string;
  readonly toutes: readonly PieceEcheancier[];
  readonly activites: EcheancierLu['activites'];
  readonly actions: ActionsEcheancier;
  readonly quandFermer: () => void;
  readonly quandOuvrir: (id: string) => void;
}) {
  const [detail, setDetail] = useState<DetailPiece | string | null>(null);
  const [lignes, setLignes] = useState<LigneSaisie[]>([]);
  const [commentaire, setCommentaire] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [cession, setCession] = useState(piece.cedee_le ?? '');
  const clients = cote === 'client';

  function recharger() {
    setDetail(null);
    void actions.detail(cote, piece.pennylane_id).then(setDetail);
  }

  useEffect(() => {
    recharger();
    setLignes(
      piece.paiements.length === 0
        ? [{ montant: '', date: '' }]
        : piece.paiements.map((p) => ({ montant: p.montant ?? '', date: p.date })),
    );
    setCession(piece.cedee_le ?? '');
    setRefus(null);
  }, [piece.pennylane_id, piece.paiements, piece.cedee_le]);

  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') quandFermer();
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [quandFermer]);

  const plusieurs = lignes.length >= 2;
  const ecart = plusieurs
    ? resteADecouper(
        lignes.map((l) => l.montant),
        piece.reste_du,
      )
    : 0;
  const complet =
    lignes.every((l) => l.date !== '') &&
    (!plusieurs || lignes.every((l) => centimes(l.montant.replace(',', '.')) > 0));
  const autres = toutes.filter(
    (p) => p.tiers.cle === piece.tiers.cle && p.pennylane_id !== piece.pennylane_id,
  );

  async function enregistrer(paiements: PaiementPrevu[]) {
    setEnvoi(true);
    setRefus(null);
    const issue = await actions.poser(
      cote,
      piece.pennylane_id,
      paiements,
      commentaire.trim() === '' ? null : commentaire.trim(),
    );
    setEnvoi(false);
    if (issue !== null) setRefus(issue);
    else {
      setCommentaire('');
      recharger();
    }
  }

  return (
    <div className="ech-voile" role="presentation" onClick={quandFermer}>
      <div
        className="ech-panneau"
        role="dialog"
        aria-modal="true"
        aria-label={`Pièce ${piece.numero ?? piece.pennylane_id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ech-panneau-tete">
          <div>
            <h2>
              {clients ? 'Facture' : 'Pièce'}{' '}
              {piece.numero ?? `n° ${piece.pennylane_id}`}
            </h2>
            <p className="ech-sous">
              {piece.tiers.nom}
              {piece.libelle !== null && ` · ${piece.libelle}`}
            </p>
          </div>
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={quandFermer}
          >
            ×
          </button>
        </div>

        <dl className="ech-fiche">
          <div>
            <dt>Date</dt>
            <dd>{dateFr(piece.date_piece)}</dd>
          </div>
          <div>
            <dt>Échéance</dt>
            <dd>
              {dateFr(piece.echeance)}
              {piece.echeance !== null && piece.echeance < aujourdhui && (
                <span className="ech-etiquette alerte">
                  échue depuis {ecartJours(piece.echeance, aujourdhui)} j
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt>Montant TTC</dt>
            <dd className="nombre">{euros(centimes(piece.montant_ttc), true)}</dd>
          </div>
          <div>
            <dt>Reste dû</dt>
            <dd className="nombre">
              <strong>{euros(centimes(piece.reste_du), true)}</strong>
            </dd>
          </div>
          <div>
            <dt>{clients ? 'Retard habituel' : 'Délai habituel'}</dt>
            <dd>
              {piece.retard.jours === null
                ? 'pas d’historique'
                : `${String(piece.retard.jours)} j (médiane de ${String(piece.retard.retenus)} règlement(s))`}
              {piece.retard.exclus > 0 && (
                <span className="ech-doux"> · {piece.retard.exclus} écarté(s)</span>
              )}
            </dd>
          </div>
          {clients && piece.promesse !== null && (
            <div>
              <dt>Promesse du client</dt>
              <dd>
                {dateFr(piece.promesse.date)}
                {piece.promesse.note !== null && ` — ${piece.promesse.note}`}
              </dd>
            </div>
          )}
        </dl>

        <section className="ech-bloc">
          <h3>
            Date{plusieurs ? 's' : ''} de {clients ? 'l’encaissement' : 'paiement'}{' '}
            prévu{plusieurs ? 'es' : 'e'}
          </h3>
          {piece.prelevement ? (
            <p className="ech-doux">
              Prélèvement : la date est l’échéance et ne se saisit pas.
            </p>
          ) : (
            <>
              <p className="ech-doux ech-petit">
                Une seule ligne : tout le reste dû à cette date. Plusieurs lignes : le
                paiement en plusieurs fois, dont le total doit faire le reste dû.
              </p>
              <table className="ech-tranches">
                <tbody>
                  {lignes.map((l, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Montant ${String(i + 1)}`}
                          placeholder={plusieurs ? 'Montant' : 'Tout le reste'}
                          disabled={!plusieurs}
                          value={plusieurs ? l.montant : ''}
                          onChange={(e) =>
                            setLignes(
                              lignes.map((x, k) =>
                                k === i ? { ...x, montant: e.target.value } : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          aria-label={`Date ${String(i + 1)}`}
                          value={l.date}
                          onChange={(e) =>
                            setLignes(
                              lignes.map((x, k) =>
                                k === i ? { ...x, date: e.target.value } : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td>
                        {plusieurs && (
                          <button
                            type="button"
                            className="ech-lien"
                            onClick={() => setLignes(lignes.filter((_, k) => k !== i))}
                          >
                            retirer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="ech-actions">
                <button
                  type="button"
                  className="bouton bouton-discret bouton-petit"
                  disabled={lignes.length >= 24}
                  onClick={() => {
                    const base =
                      lignes.length === 1
                        ? [{ ...lignes[0], montant: '' } as LigneSaisie]
                        : lignes;
                    setLignes([...base, { montant: '', date: '' }]);
                  }}
                >
                  Ajouter un paiement
                </button>
                {plusieurs && (
                  <span className={ecart === 0 ? 'ech-ok' : 'ech-ko'}>
                    {ecart === 0
                      ? 'Le total fait le reste dû.'
                      : ecart > 0
                        ? `Il manque ${euros(ecart, true)}.`
                        : `${euros(-ecart, true)} de trop.`}
                  </span>
                )}
              </div>
              <label className="ech-champ ech-large">
                Commentaire (facultatif)
                <input
                  type="text"
                  maxLength={500}
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                />
              </label>
              {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
              <div className="ech-actions">
                <button
                  type="button"
                  className="bouton bouton-petit"
                  disabled={envoi || !complet || ecart !== 0}
                  onClick={() =>
                    void enregistrer(
                      plusieurs
                        ? lignes.map((l) => ({
                            montant: l.montant.replace(',', '.'),
                            date: l.date,
                          }))
                        : [{ montant: null, date: lignes[0]?.date ?? '' }],
                    )
                  }
                >
                  Enregistrer
                </button>
                {piece.paiements.length > 0 && (
                  <button
                    type="button"
                    className="bouton bouton-discret bouton-petit"
                    disabled={envoi}
                    onClick={() => void enregistrer([])}
                  >
                    Revenir à la date calculée
                  </button>
                )}
              </div>
            </>
          )}
        </section>

        {clients ? (
          <section className="ech-bloc">
            <h3>Affacturage</h3>
            <label className="ech-bascule">
              <input
                type="checkbox"
                checked={piece.cedee_le !== null}
                disabled={envoi}
                onChange={(e) => {
                  const date = e.target.checked
                    ? cession !== ''
                      ? cession
                      : ajouterJours(piece.date_piece ?? aujourdhui, 2)
                    : null;
                  setEnvoi(true);
                  void actions.ceder(piece.pennylane_id, date).then((issue) => {
                    setEnvoi(false);
                    setRefus(issue);
                  });
                }}
              />
              Facture cédée au factor
            </label>
            {piece.cedee_le !== null && (
              <label className="ech-champ">
                Date de cession
                <input
                  type="date"
                  value={cession}
                  onChange={(e) => setCession(e.target.value)}
                  onBlur={() => {
                    if (cession !== '' && cession !== piece.cedee_le) {
                      void actions.ceder(piece.pennylane_id, cession).then(setRefus);
                    }
                  }}
                />
              </label>
            )}
            <p className="ech-doux ech-petit">
              Proposée : date de la facture + 2 jours, à corriger si la cession a eu
              lieu un autre jour.
            </p>
          </section>
        ) : (
          <section className="ech-bloc">
            <h3>Réglage du fournisseur</h3>
            {piece.tiers.cle === 'sans-tiers' ? (
              <p className="ech-doux">Pièce sans fournisseur chez Pennylane.</p>
            ) : (
              <>
                <label className="ech-bascule">
                  <input
                    type="checkbox"
                    checked={piece.prelevement}
                    disabled={envoi}
                    onChange={(e) => {
                      setEnvoi(true);
                      void actions
                        .reglerFournisseur(piece.tiers.cle, {
                          prelevement: e.target.checked,
                        })
                        .then((issue) => {
                          setEnvoi(false);
                          setRefus(issue);
                        });
                    }}
                  />
                  Payé par prélèvement (la date est l’échéance)
                </label>
                <p className="ech-doux ech-petit">
                  Mode lu chez Pennylane : {piece.mode_paiement ?? 'non renseigné'}.
                </p>
                <label className="ech-champ">
                  Activité du fournisseur
                  <select
                    value={piece.activite_id ?? ''}
                    disabled={envoi}
                    onChange={(e) => {
                      setEnvoi(true);
                      void actions
                        .reglerFournisseur(piece.tiers.cle, {
                          centre_id: e.target.value === '' ? null : e.target.value,
                        })
                        .then((issue) => {
                          setEnvoi(false);
                          setRefus(issue);
                        });
                    }}
                  >
                    <option value="">Sans activité</option>
                    {activites.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nom}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
          </section>
        )}

        <section className="ech-bloc">
          <h3>Historique des dates prévues</h3>
          {detail === null ? (
            <p className="attente">Lecture…</p>
          ) : typeof detail === 'string' ? (
            <p className="bandeau bandeau-refus">{detail}</p>
          ) : detail.historique.length === 0 ? (
            <p className="ech-doux">
              Aucun changement : la date a toujours été calculée.
            </p>
          ) : (
            <ul className="ech-historique">
              {detail.historique.map((h, i) => (
                <li key={i}>
                  <span className="ech-doux ech-petit">
                    {dateFr(h.le.slice(0, 10))} · {h.par}
                  </span>
                  <span>
                    {enMots(h.avant)} → <strong>{enMots(h.apres)}</strong>
                  </span>
                  {h.commentaire !== null && (
                    <em className="ech-petit">« {h.commentaire} »</em>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {autres.length > 0 && (
          <section className="ech-bloc">
            <h3>Autres pièces ouvertes de {piece.tiers.nom}</h3>
            <ul className="ech-liste">
              {autres.map((a) => (
                <li key={a.pennylane_id}>
                  <button
                    type="button"
                    className="ech-numero"
                    onClick={() => quandOuvrir(a.pennylane_id)}
                  >
                    {a.numero ?? `n° ${a.pennylane_id}`}
                  </button>
                  <span className="ech-doux">échéance {dateFr(a.echeance)}</span>
                  <span className="nombre">{euros(centimes(a.reste_du), true)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {detail !== null &&
          typeof detail !== 'string' &&
          detail.reglements.length > 0 && (
            <section className="ech-bloc">
              <h3>Derniers règlements</h3>
              <ul className="ech-liste">
                {detail.reglements.map((r, i) => (
                  <li key={`${r.numero ?? ''}-${String(i)}`}>
                    <span>{r.numero ?? '—'}</span>
                    <span className="ech-doux">réglée le {dateFr(r.reglee_le)}</span>
                    <span
                      className={
                        r.echeance !== null && r.reglee_le > r.echeance
                          ? 'ech-ko'
                          : 'ech-ok'
                      }
                    >
                      {r.echeance === null
                        ? 'sans échéance'
                        : `${ecartJours(r.echeance, r.reglee_le) > 0 ? '+' : ''}${String(ecartJours(r.echeance, r.reglee_le))} j`}
                    </span>
                    <span className="nombre">
                      {euros(centimes(r.montant_ttc), true)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
      </div>
    </div>
  );
}

function enMots(paiements: readonly PaiementPrevu[]): string {
  if (paiements.length === 0) return 'date calculée';
  if (paiements.length === 1 && paiements[0]?.montant === null)
    return dateFr(paiements[0].date);
  return `${String(paiements.length)} paiements (${paiements
    .map((p) => `${euros(centimes(p.montant), true)} le ${dateCourte(p.date)}`)
    .join(', ')})`;
}
